import type { RgbaImage } from "./analyzeImage";

/** Largest dimension (px) an image is scaled down to before analysis, for speed. */
export const DEFAULT_MAX_DIMENSION = 1000;

/** Load a `File` (e.g. a chosen photo) into a decoded `HTMLImageElement`. */
export function fileToImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not decode the selected image."));
    };
    img.src = url;
  });
}

/**
 * Draw an image onto a canvas (downscaling very large photos) and read back its
 * RGBA pixels for analysis.
 */
export function imageToRgba(
  source: CanvasImageSource & { width: number; height: number },
  maxDimension: number = DEFAULT_MAX_DIMENSION,
): RgbaImage {
  const { width: srcW, height: srcH } = source;
  const scale = Math.min(1, maxDimension / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context is not available in this browser.");

  ctx.drawImage(source, 0, 0, width, height);
  const { data } = ctx.getImageData(0, 0, width, height);
  return { data, width, height };
}
